/* eslint-disable quotes */
/* eslint-disable no-underscore-dangle */
// eslint-disable-next-line import/no-unresolved
import introJs from "intro.js";
import React, { useEffect, useRef, useState } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hasPermission, translate, Permissions } from "../../../../../providers";
import {
	ROOT_DIR,
	setLoadingLocation,
	setSearchDocumentId,
} from "../../../../../../views/document-center/slice/document-center-slice";
import { DocumentService } from "../../../../../../api";
import { useApi } from "../../../../../hooks";
import { isElementPlaced } from "../../../utils/utils";

const checkNotFolderTr = () => {
	const tbody = document.querySelector("tbody.MuiTableBody-root");
	let hasTargetRow = false;
	if (tbody) {
		const rows = Array.from(tbody.querySelectorAll("tr"));
		hasTargetRow = rows.some(
			(row) => !Array.from(row.classList).some((cls) => cls.startsWith("DocumentCenterTable_docCenterFolder__"))
		);
	}
	return hasTargetRow;
};

const DocumentCenterTour = ({ activatedTour, currentTour, onClose, onCompleteTour }) => {
	const [documentInformation, setDocumentInformation] = useState(null);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const startingProductTourDocumentId = useSelector(({ context }) => context?.startingProductTourDocumentId);
	const hasCompletedDocument = useSelector(({ documentCenter }) =>
		documentCenter?.currentDirectoryContent?.contents?.some(
			(doc) => doc.type !== "folder" && doc.status === "COMPLETED"
		)
	);
	const { call: getInformation } = useApi(DocumentService.getInformation);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const introRef = useRef(null);
	const calledRef = useRef(false);

	useEffect(() => {
		if (calledRef.current || (hasCompletedDocument && startingProductTourDocumentId)) {
			return;
		}
		if (activatedTour === currentTour.id && startingProductTourDocumentId && !hasCompletedDocument) {
			getInformation({ docId: startingProductTourDocumentId })
				.then((info) => {
					setDocumentInformation(info);
					const id = info?.directoryId ?? ROOT_DIR;
					const url =
						id === ROOT_DIR
							? `/projects/${projectId}/documents`
							: `/projects/${projectId}/documents?directory=${id}`;
					const documentId = startingProductTourDocumentId;
					setTimeout(() => {
						navigate(url);
					}, 0);
					batch(() => {
						dispatch(setLoadingLocation(true));
						dispatch(setSearchDocumentId(+documentId || null));
						calledRef.current = true;
					});
				})
				.catch((error) => {
					console.error(error);
				});
		} else if (activatedTour === currentTour.id && hasCompletedDocument) {
			calledRef.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		getInformation,
		dispatch,
		projectId,
		activatedTour,
		currentTour,
		startingProductTourDocumentId,

		hasCompletedDocument,
	]);
	if (!introRef?.current) {
		introRef.current = introJs();
	}
	useEffect(
		() => {
			introRef.current.setOptions({
				disableInteraction: true,
				nextLabel: translate("common:btn.next"),
				prevLabel: translate("common:btn.previous"),
				doneLabel: translate("common:btn.finish"),
				exitOnEsc: false,
				exitOnOverlayClick: false,
				hidePrev: true,
				keyboardNavigation: false,
				sanitize: false,
				helperElementPadding: 8,
				steps: [
					{
						step: 0,
						title: translate("common:product-tour.essential.documents.step.1.title"),
						intro: translate("common:product-tour.essential.documents.step.1.intro"),
						element: Array.from(
							document.querySelectorAll('span[class^="Navbar_navbar__projectManagementMenus__"] button')
						).find((btn) =>
							btn.textContent.includes(translate("navigation:project.project-configuration"))
						),
						position: "bottom",
					},
					{
						step: 1,
						title: translate("common:product-tour.essential.documents.step.2.title"),
						intro: translate("common:product-tour.essential.documents.step.2.intro"),
						element: "div[class^='DocumentCenterMenu_buttonsContainer__']",
						permissions: [Permissions.PROJECT_MANAGER, Permissions.PROJECT_LEADER],
						position: "right",
					},
					{
						step: 2,
						title: translate("common:product-tour.essential.documents.step.3.title"),
						intro: translate("common:product-tour.essential.documents.step.3.intro"),
						element: "div[class^='DocumentCenterMenu_tree__']",
					},
					{
						step: 3,
						title: translate("common:product-tour.essential.documents.step.4.title"),
						intro: translate("common:product-tour.essential.documents.step.4.intro"),
						element: "div[class^='MuiAutocomplete-root",
					},
					{
						step: 4,
						title: translate("common:product-tour.essential.documents.step.5.title"),
						intro: translate("common:product-tour.essential.documents.step.5.intro"),
						permissions: [Permissions.PROJECT_MANAGER, Permissions.PROJECT_LEADER],
						element: "tbody tr:last-of-type td span input",
					},
					{
						step: 5,
						title: translate("common:product-tour.essential.documents.step.6.title"),
						intro: translate("common:product-tour.essential.documents.step.6.intro"),
						permissions: [Permissions.PROJECT_MANAGER, Permissions.PROJECT_LEADER],
						element: "div[class^='DocumentCenter_main__search__'] > button:last-of-type",
						position: "left",
					},
					{
						step: 6,
						title: translate("common:product-tour.essential.documents.step.7.title"),
						intro: translate(
							(hasPermission([Permissions.PROJECT_MANAGER, Permissions.PROJECT_LEADER]) &&
								"common:product-tour.essential.documents.step.7.intro") ||
								"common:product-tour.essential.documents.step.7.intro.member"
						),
						element: "tbody tr:last-of-type",
					},
				].filter((step) => !step?.permissions || hasPermission(step?.permissions)),
				tooltipClass: "IntroJsClass",
				showBullets: false,
				showProgress: false,
				showStepNumbers: false,
			});
			introRef.current.onafterchange(() => {
				const currentStepIndex = introRef.current._currentStep;
				const totalSteps = introRef.current._options.steps.length;
				const tooltipButtons = document.querySelector(".introjs-tooltipbuttons");
				if (!tooltipButtons) {
					return;
				}
				const existingCounter = tooltipButtons.querySelector(".custom-step-counter");
				if (existingCounter) {
					existingCounter.remove();
				}
				const counter = document.createElement("div");
				counter.classList.add("custom-step-counter");
				counter.textContent = translate("common:product-tour.steps", {
					current: currentStepIndex + 1,
					total: totalSteps,
				});
				tooltipButtons.insertBefore(counter, tooltipButtons.firstChild);
			});
			const handleExit = () => {
				onClose(currentTour);
				introRef.current = null;
			};
			introRef.current.onexit(() => {
				handleExit();
			});
			introRef.current.oncomplete((_, reason) => {
				if (reason === "done") {
					onCompleteTour(currentTour);
				}
				handleExit();
			});
			const intervalId = setInterval(() => {
				const targetDiv = document.querySelector("div[class^='DocumentCenter_main__table__']");
				const hasAtLeastOneRow = checkNotFolderTr();
				const tourCondition = hasCompletedDocument && isElementPlaced(targetDiv) && hasAtLeastOneRow;
				if (activatedTour === currentTour.id && tourCondition) {
					clearInterval(intervalId);
					setTimeout(() => {
						introRef.current.start();
					}, 400);
				}
			}, 100);
			return () => {
				clearInterval(intervalId);
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			navigate,
			dispatch,
			introRef?.current,
			onClose,
			onCompleteTour,
			activatedTour,
			currentTour,
			documentInformation,
			hasCompletedDocument,
		]
	);
	return <></>;
};

export default DocumentCenterTour;
